import { type PropsWithChildren } from "react";

import { useShellLayout } from "../context";
import { cx } from "../cx";
import { useFullscreenTransitionMask } from "../hooks/useFullscreenTransitionMask";
import { useIsResizable } from "../ShellBody/ResizableContext";
import { Body } from "./Body";
import { Footer } from "./Footer";
import { Header } from "./Header";

interface RootProps {
  "aria-label": string;
}

const Root = ({
  children,
  "aria-label": ariaLabel,
}: Readonly<PropsWithChildren<RootProps>>) => {
  const isResizable = useIsResizable();
  const { conversationPanelState } = useShellLayout();
  const {
    isRevealing,
    isTransitionMaskOpaque,
    completeGeometryTransition,
    completeMaskReveal,
  } = useFullscreenTransitionMask(conversationPanelState);
  const isAriaHidden = conversationPanelState === "fullscreen";
  const isFlexCollapsed =
    isResizable && conversationPanelState === "fullscreen";
  const isMaskOpaque = isAriaHidden || isTransitionMaskOpaque;
  const isCanvasInteractionBlocked = isMaskOpaque || isRevealing;

  return (
    <section
      data-testid="circle-ai-shell-canvas"
      aria-label={ariaLabel}
      aria-hidden={isCanvasInteractionBlocked || undefined}
      inert={isCanvasInteractionBlocked || undefined}
      className={cx(
        "relative isolate flex h-full min-h-0 min-w-0 flex-col overflow-hidden",
        {
          "flex-[0_0_0px]": isFlexCollapsed,
          "flex-[1_1_0%]": !isFlexCollapsed,
          "[transition:grow_280ms_cubic-bezier(0.77,0,0.175,1)] motion-reduce:transition-none":
            isResizable,
        },
      )}
      onTransitionEnd={event => {
        if (
          event.currentTarget === event.target &&
          event.propertyName === "flex-grow"
        ) {
          completeGeometryTransition();
        }
      }}
    >
      {children}
      <div
        aria-hidden
        className={cx(
          "pointer-events-none absolute inset-0 z-10 bg-primary dark:bg-secondary",
          {
            "opacity-100 transition-none": isMaskOpaque,
            "opacity-0": !isMaskOpaque,
            "duration-[280ms] transition-opacity ease-in-out":
              isRevealing && !isMaskOpaque,
          },
        )}
        onTransitionEnd={event => {
          if (
            event.currentTarget === event.target &&
            event.propertyName === "opacity"
          ) {
            completeMaskReveal();
          }
        }}
      />
    </section>
  );
};

Root.displayName = "CircleAIAppShell.Layout.Canvas";

export const Canvas = Object.assign(Root, {
  Header,
  Body,
  Footer,
});
